import { useEffect, useRef, useState } from "react";
import { CalendarDays, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";

const isoToIt = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || "");
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
};
const itToIso = (s) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s);
  if (!m) return null;
  const dd = +m[1], mm = +m[2], yyyy = +m[3];
  const d = new Date(yyyy, mm - 1, dd);
  if (d.getFullYear() !== yyyy || d.getMonth() !== mm - 1 || d.getDate() !== dd) return null;
  return `${m[3]}-${m[2]}-${m[1]}`;
};
const maskDate = (v) => {
  const d = v.replace(/\D/g, "").slice(0, 8);
  let out = d.slice(0, 2);
  if (d.length >= 3) out += "/" + d.slice(2, 4);
  if (d.length >= 5) out += "/" + d.slice(4, 8);
  return out;
};
const maskTime = (v) => {
  const d = v.replace(/\D/g, "").slice(0, 4);
  let out = d.slice(0, 2);
  if (d.length >= 3) out += ":" + d.slice(2, 4);
  return out;
};
const validTime = (s) => /^([01]\d|2[0-3]):[0-5]\d$/.test(s);

// Campo DATA digitabile da tastiera (gg/mm/aaaa) + selettore nativo via icona. value/onChange in ISO yyyy-mm-dd.
export function DateField({ value, onChange, testid }) {
  const [text, setText] = useState(isoToIt(value));
  const nativeRef = useRef(null);
  useEffect(() => { setText(isoToIt(value)); }, [value]);

  const handleText = (e) => {
    const masked = maskDate(e.target.value);
    setText(masked);
    if (masked === "") { onChange(""); return; }
    const iso = itToIso(masked);
    if (iso) onChange(iso);
  };
  const openPicker = () => {
    const el = nativeRef.current;
    if (!el) return;
    if (el.showPicker) el.showPicker(); else el.focus();
  };
  return (
    <div className="relative">
      <Input value={text} onChange={handleText} placeholder="gg/mm/aaaa" inputMode="numeric" maxLength={10}
        data-testid={testid} className="pr-8" />
      <button type="button" onClick={openPicker} tabIndex={-1} aria-label="Apri calendario"
        className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary" data-testid={testid ? `${testid}-picker` : undefined}>
        <CalendarDays size={16} />
      </button>
      <input ref={nativeRef} type="date" value={value || ""} onChange={(e) => onChange(e.target.value)}
        tabIndex={-1} aria-hidden="true"
        className="pointer-events-none absolute right-2 top-1/2 h-0 w-0 -translate-y-1/2 opacity-0" />
    </div>
  );
}

// Campo ORA digitabile da tastiera (hh:mm) + selettore nativo via icona. value/onChange in HH:MM.
export function TimeField({ value, onChange, testid }) {
  const [text, setText] = useState(value || "");
  const nativeRef = useRef(null);
  useEffect(() => { setText(value || ""); }, [value]);

  const handleText = (e) => {
    const masked = maskTime(e.target.value);
    setText(masked);
    if (masked === "") { onChange(""); return; }
    if (validTime(masked)) onChange(masked);
  };
  const openPicker = () => {
    const el = nativeRef.current;
    if (!el) return;
    if (el.showPicker) el.showPicker(); else el.focus();
  };
  return (
    <div className="relative">
      <Input value={text} onChange={handleText} placeholder="hh:mm" inputMode="numeric" maxLength={5}
        data-testid={testid} className="pr-8" />
      <button type="button" onClick={openPicker} tabIndex={-1} aria-label="Apri orologio"
        className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary" data-testid={testid ? `${testid}-picker` : undefined}>
        <Clock size={16} />
      </button>
      <input ref={nativeRef} type="time" value={value || ""} onChange={(e) => onChange(e.target.value)}
        tabIndex={-1} aria-hidden="true"
        className="pointer-events-none absolute right-2 top-1/2 h-0 w-0 -translate-y-1/2 opacity-0" />
    </div>
  );
}
